import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  AddressFieldDefinition,
  AddressValue,
  ChoiceFieldDefinition,
  DateFieldDefinition,
  DropdownFieldDefinition,
  FileUploadFieldDefinition,
  FormFieldDefinition,
  TimeFieldDefinition,
} from "@rwaforms/shared";
import { evaluateConditionalLogic, computeCalculatedOutputs, INDIAN_STATES } from "@rwaforms/shared";
import { createPreviewToken, fetchFormDetail } from "../lib/api-client";
import { FileUploadField } from "../components/FileUploadField";

function FormPreviewPage(): JSX.Element {
  const { formId } = useParams<{ formId: string }>();
  const detailQuery = useQuery({
    queryKey: ["form", formId],
    queryFn: () => fetchFormDetail(formId!),
    enabled: Boolean(formId),
  });

  const schema = detailQuery.data?.draft?.schema ?? detailQuery.data?.publishedVersion?.schema;
  const [answers, setAnswers] = useState<Record<string, unknown>>({});

  const logic = useMemo(() => {
    if (!schema) return null;
    return evaluateConditionalLogic(schema, answers);
  }, [schema, answers]);

  const versionId = detailQuery.data?.draft?.versionId ?? detailQuery.data?.publishedVersion?.id;

  const previewTokenMutation = useMutation({
    mutationFn: async () => {
      if (!versionId) {
        throw new Error("Version unavailable");
      }
      return createPreviewToken(versionId);
    },
  });

  const calculated = useMemo(() => {
    if (!schema) return {};
    return computeCalculatedOutputs(schema, answers);
  }, [schema, answers]);

  if (!formId) {
    return <div style={{ padding: "2rem" }}>Form not specified.</div>;
  }

  if (detailQuery.isLoading || !schema) {
    return <div style={{ padding: "2rem" }}>Preparing live preview...</div>;
  }

  const hiddenFields = logic?.hiddenFieldIds ?? new Set<string>();

  return (
    <div style={{ padding: "1.5rem 2rem", display: "grid", gap: "1.5rem" }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem" }}>
        <div>
          <h1 style={{ margin: 0 }}>{detailQuery.data?.form.title || "Untitled form"}</h1>
          <p style={{ margin: "0.35rem 0 0", color: "var(--color-text-muted)", maxWidth: "60ch" }}>
            Preview reflects branching rules and calculated outputs in real time. Test with sample responses before publishing.
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", justifyContent: "flex-end" }}>
          <Link to=".." relative="path" className="button ghost">
            Back to builder
          </Link>
          {versionId ? (
            <button
              type="button"
              className="button secondary"
              disabled={previewTokenMutation.isPending}
              onClick={() => previewTokenMutation.mutate()}
            >
              {previewTokenMutation.isPending ? "Generating…" : "Share preview token"}
            </button>
          ) : null}
        </div>
      </header>

      {previewTokenMutation.isSuccess && previewTokenMutation.data ? (
        <div className="surface" style={{ padding: "0.75rem", fontSize: "0.85rem", display: "flex", gap: "0.75rem", alignItems: "center" }}>
          <span>Preview token: {(previewTokenMutation.data as { token: string }).token}</span>
          <button
            type="button"
            className="button ghost"
            style={{ padding: "0.25rem 0.6rem", fontSize: "0.75rem" }}
            onClick={() => navigator.clipboard.writeText((previewTokenMutation.data as { token: string }).token)}
          >
            Copy
          </button>
        </div>
      ) : null}

      {/* Form Header - Title & Description (Google Forms style) */}
      <div className="surface" style={{
        padding: "0",
        borderRadius: "8px",
        overflow: "hidden"
      }}>
        <div style={{
          height: "10px",
          background: "var(--color-primary, #4f46e5)",
          borderTopLeftRadius: "8px",
          borderTopRightRadius: "8px"
        }} />
        <div style={{ padding: "1.5rem 2rem" }}>
          <h2 style={{
            margin: 0,
            fontSize: "1.75rem",
            fontWeight: 600,
            color: "var(--color-text)"
          }}>
            {detailQuery.data?.form.title || "Untitled form"}
          </h2>
          {detailQuery.data?.form.description && (
            <p style={{
              margin: "0.75rem 0 0",
              color: "var(--color-text-muted)",
              fontSize: "1rem",
              lineHeight: 1.6,
              whiteSpace: "pre-wrap"
            }}>
              {detailQuery.data.form.description}
            </p>
          )}
        </div>
      </div>

      <div className="surface" style={{ padding: "1.5rem", display: "grid", gap: "1rem" }}>
        {schema.fields.map((field) => {
          if (hiddenFields.has(field.id)) {
            return null;
          }
          const value =
            field.type === "calculated"
              ? calculated[field.id] ?? ""
              : (answers[field.id] as string | number | boolean | string[] | undefined);
          return (
            <FieldPreview
              key={field.id}
              formId={formId}
              field={field}
              value={value}
              onChange={(newValue) =>
                setAnswers((current) => ({
                  ...current,
                  [field.id]: newValue,
                }))
              }
            />
          );
        })}
      </div>
    </div>
  );
}

function FieldPreview({
  formId,
  field,
  value,
  onChange,
}: {
  formId: string;
  field: FormFieldDefinition;
  value: unknown;
  onChange(value: unknown): void;
}): JSX.Element {

  const label = (
    <div style={{ marginBottom: "0.5rem" }}>
      <p style={{ margin: 0, fontWeight: 600 }}>{field.title || "Untitled field"}</p>
      {field.description ? (
        <p style={{ margin: "0.2rem 0 0", color: "var(--color-text-muted)", fontSize: "0.85rem" }}>{field.description}</p>
      ) : null}
    </div>
  );

  switch (field.type) {
    case "short_text":
    case "long_text":
      return (
        <div>
          {label}
          <textarea
            value={String(value ?? "")}
            onChange={(event) => onChange(event.target.value)}
            rows={field.type === "long_text" ? 4 : 2}
            style={INPUT_STYLE}
          />
        </div>
      );
    case "number":
      return (
        <div>
          {label}
          <input
            type="number"
            value={value === undefined ? "" : String(value)}
            onChange={(event) => onChange(event.target.value ? Number(event.target.value) : "")}
            style={INPUT_STYLE}
          />
        </div>
      );
    case "email":
      return (
        <div>
          {label}
          <input
            type="email"
            value={String(value ?? "")}
            onChange={(event) => onChange(event.target.value)}
            placeholder="your@email.com"
            style={INPUT_STYLE}
          />
        </div>
      );
    case "phone":
      return (
        <div>
          {label}
          <input
            type="tel"
            value={String(value ?? "")}
            onChange={(event) => onChange(event.target.value)}
            placeholder="+91 98765 43210"
            style={INPUT_STYLE}
          />
        </div>
      );
    case "name":
      return (
        <div>
          {label}
          <input
            type="text"
            value={String(value ?? "")}
            onChange={(event) => onChange(event.target.value)}
            placeholder="Full name"
            style={INPUT_STYLE}
          />
        </div>
      );
    case "multiple_choice": {
      const choiceField = field as ChoiceFieldDefinition;
      return (
        <div>
          {label}
          <div style={{ display: "grid", gap: "0.35rem" }}>
            {choiceField.options?.map((option) => (
              <label key={option.id} style={{ display: "flex", gap: "0.6rem" }}>
                <input
                  type="radio"
                  name={field.id}
                  checked={value === option.id}
                  onChange={() => onChange(option.id)}
                />
                {option.label}
              </label>
            ))}
          </div>
        </div>
      );
    }
    case "checkboxes": {
      const choiceField = field as ChoiceFieldDefinition;
      const selected = new Set(Array.isArray(value) ? (value as string[]) : []);
      return (
        <div>
          {label}
          <div style={{ display: "grid", gap: "0.35rem" }}>
            {choiceField.options?.map((option) => (
              <label key={option.id} style={{ display: "flex", gap: "0.6rem" }}>
                <input
                  type="checkbox"
                  checked={selected.has(option.id)}
                  onChange={(event) => {
                    const next = new Set(selected);
                    if (event.target.checked) {
                      next.add(option.id);
                    } else {
                      next.delete(option.id);
                    }
                    onChange(Array.from(next));
                  }}
                />
                {option.label}
              </label>
            ))}
          </div>
        </div>
      );
    }
    case "dropdown": {
      const dropdown = field as DropdownFieldDefinition;
      return (
        <div>
          {label}
          <select
            value={String(value ?? "")}
            onChange={(event) => onChange(event.target.value)}
            style={INPUT_STYLE}
          >
            <option value="">Select…</option>
            {dropdown.options?.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      );
    }
    case "file_upload": {
      const uploadField = field as FileUploadFieldDefinition;
      return (
        <FileUploadField
          formId={formId}
          field={uploadField}
          value={value as string | string[] | undefined}
          onChange={(newValue) => onChange(newValue)}
        />
      );
    }
    case "date": {
      const dateField = field as DateFieldDefinition;
      return (
        <div>
          {label}
          <input
            type="date"
            value={value ? String(value) : ""}
            onChange={(event) => onChange(event.target.value || undefined)}
            min={dateField.validation?.min}
            max={dateField.validation?.max}
            style={INPUT_STYLE}
          />
        </div>
      );
    }
    case "time": {
      const timeField = field as TimeFieldDefinition;
      return (
        <div>
          {label}
          <input
            type="time"
            value={value ? String(value) : ""}
            onChange={(event) => onChange(event.target.value || undefined)}
            style={INPUT_STYLE}
          />
        </div>
      );
    }
    case "calculated":
      return (
        <div>
          {label}
          <div style={{ padding: "0.6rem 0.75rem", background: "var(--color-bg)", borderRadius: "10px" }}>
            {String(value ?? "0")}
          </div>
        </div>
      );
    case "static_text": {
      const staticField = field as FormFieldDefinition & { content?: string; format?: string };
      return (
        <div style={{ padding: "0.5rem 0" }}>
          <p style={{ margin: 0, lineHeight: 1.6, color: "var(--color-text)" }}>
            {staticField.content || ""}
          </p>
        </div>
      );
    }
    case "address": {
      const addressField = field as AddressFieldDefinition;
      const addressValue = (value as AddressValue) || {};
      const labels = addressField.labels || {};
      const placeholders = addressField.placeholders || {};
      const defaultCountry = addressField.defaultCountry || "India";

      const updateAddressField = (subField: keyof AddressValue, subValue: string) => {
        onChange({
          ...addressValue,
          [subField]: subValue,
        });
      };

      return (
        <div>
          {label}
          <div style={{ display: "grid", gap: "0.5rem" }}>
            {/* House Number */}
            <div>
              <label style={{ display: "block", marginBottom: "0.25rem", fontSize: "0.85rem", fontWeight: 500 }}>
                {labels.houseNumber || "House Number"}
              </label>
              <input
                type="text"
                value={addressValue.houseNumber || ""}
                onChange={(e) => updateAddressField("houseNumber", e.target.value)}
                placeholder={placeholders.houseNumber || "Enter house/flat number"}
                style={INPUT_STYLE}
              />
            </div>

            {/* Two-column layout for Village and Post */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
              {/* Village */}
              <div>
                <label style={{ display: "block", marginBottom: "0.25rem", fontSize: "0.85rem", fontWeight: 500 }}>
                  {labels.village || "Village"}
                </label>
                <input
                  type="text"
                  value={addressValue.village || ""}
                  onChange={(e) => updateAddressField("village", e.target.value)}
                  placeholder={placeholders.village || "Enter village name"}
                  style={INPUT_STYLE}
                />
              </div>

              {/* Post */}
              <div>
                <label style={{ display: "block", marginBottom: "0.25rem", fontSize: "0.85rem", fontWeight: 500 }}>
                  {labels.post || "Post"}
                </label>
                <input
                  type="text"
                  value={addressValue.post || ""}
                  onChange={(e) => updateAddressField("post", e.target.value)}
                  placeholder={placeholders.post || "Enter post office name"}
                  style={INPUT_STYLE}
                />
              </div>
            </div>

            {/* Two-column layout for District and State */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
              <div>
                <label style={{ display: "block", marginBottom: "0.25rem", fontSize: "0.85rem", fontWeight: 500 }}>
                  {labels.district || "District"}
                </label>
                <input
                  type="text"
                  value={addressValue.district || ""}
                  onChange={(e) => updateAddressField("district", e.target.value)}
                  placeholder={placeholders.district || "Enter district name"}
                  style={INPUT_STYLE}
                />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: "0.25rem", fontSize: "0.85rem", fontWeight: 500 }}>
                  {labels.state || "State"}
                </label>
                <select
                  value={addressValue.state || ""}
                  onChange={(e) => updateAddressField("state", e.target.value)}
                  style={INPUT_STYLE}
                >
                  <option value="">Select State</option>
                  {INDIAN_STATES.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Two-column layout for Country and Pincode */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
              <div>
                <label style={{ display: "block", marginBottom: "0.25rem", fontSize: "0.85rem", fontWeight: 500 }}>
                  {labels.country || "Country"}
                </label>
                <input
                  type="text"
                  value={addressValue.country || defaultCountry}
                  readOnly
                  style={{ ...INPUT_STYLE, backgroundColor: "var(--color-surface-elevated, var(--color-bg))", opacity: 0.7, cursor: "not-allowed" }}
                />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: "0.25rem", fontSize: "0.85rem", fontWeight: 500 }}>
                  {labels.pincode || "Pincode"}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={addressValue.pincode || ""}
                  onChange={(e) => {
                    const numericValue = e.target.value.replace(/[^0-9]/g, "");
                    updateAddressField("pincode", numericValue);
                  }}
                  placeholder={placeholders.pincode || "Enter 6-digit pincode"}
                  style={INPUT_STYLE}
                />
              </div>
            </div>
          </div>
        </div>
      );
    }
    default:
      return (
        <div>
          {label}
          <div style={{ color: "var(--color-text-muted)", fontSize: "0.85rem" }}>
            Preview not yet available for this field type.
          </div>
        </div>
      );
  }
}

const INPUT_STYLE: React.CSSProperties = {
  width: "100%",
  borderRadius: "10px",
  border: "1px solid var(--color-border)",
  padding: "0.6rem 0.75rem",
  fontSize: "0.95rem",
  resize: "vertical",
};

export default FormPreviewPage;
